import {
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

const INPUT_FIELD_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SelectWithSearchComponent),
  multi: true,
};

@Component({
  selector: 'select-with-search',
  templateUrl: './select-with-search.component.html',
  styleUrls: ['./select-with-search.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [INPUT_FIELD_VALUE_ACCESSOR],
})
export class SelectWithSearchComponent implements ControlValueAccessor, OnInit, OnChanges {
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef<HTMLInputElement>;

  @Input() items: any[] = [];
  @Input() fieldDisplayedInMatOption: string;
  @Input() floatLabel: null | string = null;
  @Input() searchLabel: null | string = null;
  @Input() noItemsFoundMessage: null | string = null;
  @Input() errorMessageRequired: null | string = null;
  @Input() archivedLabel: null | string = null;
  @Input() fxFlex = 100;
  @Input() disableOptionIf = null;

  @Input() isMultiple = false;
  @Input() disabled = false;
  @Input() required = false;
  @Input() underlineArchivedItems = false;
  @Input() returnValueInObject = false;
  @Input() appearance: 'outline' | 'legacy' | 'fill' | 'standard' = 'legacy';

  selectedValue: FormControl = new FormControl();
  searchValue: FormControl = new FormControl();
  filteredItemsIds: string[] = [];

  constructor() {
    this.selectedValue.valueChanges.pipe(debounceTime(100)).subscribe((value) => {
      if (value){
        this.onChange(value);
      }
    });
  }

  ngOnInit(): void {
    this.filteredItemsIds = this.items.map((item) => item.id);

    if (this.required) {
      this.selectedValue.setValidators(Validators.required);
    }

    this.searchValue.valueChanges.subscribe((value: null | string): void => {
      if (!value) {
        this.filteredItemsIds = this.items.map((item) => item.id);
        return;
      }

      this.filteredItemsIds = this.items.filter((item) => {
        const itemField = item[this.fieldDisplayedInMatOption].toLowerCase();
        return itemField.includes(value.toLowerCase());
      }).map((item) => item.id);
    });
  }

  ngOnChanges(): void {
    this.filteredItemsIds = this.items.map((item) => item.id);
  }

  searchInputFocus(toClean: boolean = false): void {
    if (toClean) {
      this.searchValue.setValue(null);
    }

    this.searchInput.nativeElement.focus();
  }

  writeValue(itemOrItems: any | any[]): void {
    this.selectedValue.setValue(itemOrItems);
  }

  onChange: (_: any) => void = () => {};
  onTouched: (_: any) => void = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  compareItems(firstItem: any, secondItem: any): boolean {
    if (firstItem === secondItem) {
        return true;
    }

    if (!firstItem || !secondItem || typeof firstItem !== 'object' || typeof secondItem !== 'object') {
        return false;
    }

    return firstItem.id === secondItem.id;
  }
}
